import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import {
  AnalyticsConstants,
  DigitalData,
} from '@digital-platform/rs-analytics/domain';
import { UtilityService } from '../utilities/utility.service';
import { WindowService } from '../utilities/window.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private platform = inject(PLATFORM_ID);
  private readonly TRACK_PAGE_VIEW = 'pageView';
  private utilityService = inject(UtilityService);
  private windowService = inject(WindowService);
  protected digitalData = this.createDigitalData();

  constructor() {
    this.initializeDigitalData();
  }

  public registerPageName(pageName: string): void {
    if (this.windowService === undefined || this.digitalData === undefined) {
      this.initializeDigitalData();
    }
    this.digitalData.page.category =
      this.utilityService.getPageCategories(pageName);
    this.digitalData.page.attributes.autotrack = 'false';
    this.digitalData.page.pageInfo.pageName = pageName;
    this.digitalData.pageName = pageName || '';

    this.satelliteTrack(this.TRACK_PAGE_VIEW);
  }

  public satelliteTrack(trackId: string): void {
    if (!isPlatformBrowser(this.platform)) {
      // skip tracking calls on SSR
      return;
    }

    if (this.windowService.nativeWindow['_satellite']?.track) {
      this.windowService.nativeWindow['_satellite'].track(trackId);
    }
  }

  private initializeDigitalData() {
    if (isPlatformBrowser(this.platform)) {
      this.windowService.nativeWindow.digitalData =
        this.windowService.nativeWindow.digitalData || this.createDigitalData();
      this.digitalData = this.windowService.nativeWindow.digitalData;
    }
  }

  protected createDigitalData(): DigitalData {
    return {
      addressGate: {
        streetAddress: '',
        zipCode: '',
      },
      carbonCalculator: {
        lob: '',
      },
      cart: {
        attributes: { initialized: true, popup: '' },
        item: [],
      },
      contactUs: {
        industryType: '',
        inquiryType: '',
      },
      page: {
        attributes: {
          autotrack: '',
        },
        pageInfo: {
          pageName: '',
        },
      },
      pageName: '',
      paymentDetails: {
        amountPaid: {},
      },
      userAction: {
        actionTrigger: '',
      },
      user: {
        wayfinder: {
          who: '',
          whatService: '',
          lookingFor: '',
        },
      },
      userProfile: {
        address: {
          street: '',
          zip: '',
        },
        attributes: {
          industryType: '',
          activeAccount: {
            category: '',
            accountNumber: '',
            name: '',
          },
        },
        state: '',
        cdhId: '',
      },
      products: {
        attributes: {},
      },
      calendar: {
        attributes: {},
      },
      serviceRequest: {
        contactMethod: '',
        containerId: '',
        containerType: '',
        numberOfLifts: '',
        orderNote: '',
        problem: '',
        requestType: '',
      },
      schedulePickup: {
        attributes: {},
      },
    };
  }

  public trackWayfinderStart() {
    this.satelliteTrack(AnalyticsConstants.EVENTS.WAYFINDER.WAYFINDER_START);
  }

  public trackWayfinderComplete(wayfinderDetails: {
    who: string;
    whatService: string;
    lookingFor: string;
  }) {
    this.digitalData.user.wayfinder = wayfinderDetails;
    this.satelliteTrack(AnalyticsConstants.EVENTS.WAYFINDER.WAYFINDER_COMPLETE);
  }

  public trackHeroCarouselStart() {
    this.satelliteTrack(
      AnalyticsConstants.EVENTS.HERO_CAROUSEL.HERO_CAROUSEL_START
    );
  }

  public trackHeroCarouselPause() {
    this.satelliteTrack(
      AnalyticsConstants.EVENTS.HERO_CAROUSEL.HERO_CAROUSEL_PAUSE
    );
  }

  public trackVideoStart() {
    this.satelliteTrack(AnalyticsConstants.EVENTS.VIDEO.VIDEO_MODAL_START);
  }

  public trackVideoPause() {
    this.satelliteTrack(AnalyticsConstants.EVENTS.VIDEO.VIDEO_MODAL_PAUSE);
  }

  public trackCarbonCalculatorStart(lob: string) {
    this.digitalData.carbonCalculator.lob = lob;
    this.satelliteTrack(
      AnalyticsConstants.EVENTS.CARBON_CALCULATOR.CARBON_CALCULATOR_START
    );
  }

  public trackCarbonCalculatorOutput() {
    this.satelliteTrack(
      AnalyticsConstants.EVENTS.CARBON_CALCULATOR.CARBON_CALCULATOR_OUTPUT
    );
  }

  public trackPardotFormSubmit(inquiryType: string, industryType: string) {
    this.digitalData.contactUs.industryType = industryType;
    this.digitalData.contactUs.inquiryType = inquiryType;
    this.satelliteTrack(AnalyticsConstants.EVENTS.CONTACT_US.CONTACT_US_SUBMIT);
  }

  public trackAddressGateSubmit(
    streetAddress: string,
    zipCode: string,
    industryType: string,
    pageName: string
  ) {
    this.digitalData.userProfile.address.street = streetAddress;
    this.digitalData.userProfile.address.zip = zipCode;
    this.digitalData.userProfile.attributes.industryType = industryType;
    this.digitalData.page.pageInfo.pageName = pageName;

    this.satelliteTrack(AnalyticsConstants.EVENTS.ADDRESS_GATE.SUBMIT);
  }
}
